import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
} from 'firebase/firestore'
import { ref, uploadString, getDownloadURL } from 'firebase/storage'
import { db, storage } from '@/lib/firebase/config'
import { createAuditLog } from '@/lib/firebase/firestore'
import type { VisitNote, VisitNoteInput, VisitNoteStatus, UserRole, VisitPhoto, VisitPhotoType, Visit } from '@/types'
import { getAuthorization, updateUnitsUsed, calculateVisitUnitsUsed } from './authorizations'
import { getVisitNoteLockStatus } from '@/lib/utils/visitNoteLock'
import { getAgencySettings } from './settings'

import { Timestamp } from 'firebase/firestore'

const COLLECTION = 'visitNotes'

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (Object.prototype.toString.call(value) !== '[object Object]') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter(item => item !== undefined)
      .map(item => stripUndefinedDeep(item)) as T
  }

  if (isPlainObject(value)) {
    const cleaned: Record<string, unknown> = {}
    for (const [key, nestedValue] of Object.entries(value)) {
      if (nestedValue !== undefined) {
        cleaned[key] = stripUndefinedDeep(nestedValue)
      }
    }
    return cleaned as T
  }

  return value
}

// Photo upload interface
interface PhotoToUpload {
  id: string
  type: VisitPhotoType
  dataUrl: string
  caption?: string
  timestamp: Date
}

// Upload visit photos to Firebase Storage
export async function uploadVisitPhotos(
  agencyId: string,
  visitId: string,
  photos: PhotoToUpload[]
): Promise<VisitPhoto[]> {
  const uploadedPhotos: VisitPhoto[] = []

  for (const photo of photos) {
    try {
      console.debug('[uploadVisitPhotos] Uploading photo')
      const photoRef = ref(
        storage,
        `agencies/${agencyId}/visit-photos/${visitId}/${photo.id}.jpg`
      )

      await uploadString(photoRef, photo.dataUrl, 'data_url')
      const photoUrl = await getDownloadURL(photoRef)
      console.debug('[uploadVisitPhotos] Photo uploaded successfully')

      uploadedPhotos.push({
        id: photo.id,
        type: photo.type,
        url: photoUrl,
        caption: photo.caption,
        timestamp: Timestamp.fromDate(photo.timestamp || new Date()),
      })
    } catch (err) {
      console.error(`[uploadVisitPhotos] Failed to upload photo ${photo.id}:`, err)
    }
  }

  return uploadedPhotos
}

interface CreateVisitNoteParams {
  data: VisitNoteInput
  agencyId: string
  caregiverId: string
  userId: string
  userEmail: string
  userRole: UserRole
}

interface UpdateVisitNoteParams {
  noteId: string
  data: Partial<VisitNote>
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
}

// Get a single visit note by ID
export async function getVisitNote(noteId: string): Promise<VisitNote | null> {
  const docRef = doc(db, COLLECTION, noteId)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) return null

  return { id: docSnap.id, ...docSnap.data() } as VisitNote
}

// Get visit note by visit ID
export async function getVisitNoteByVisitId(visitId: string, agencyId: string): Promise<VisitNote | null> {
  const q = query(
    collection(db, COLLECTION),
    where('visitId', '==', visitId),
    where('agencyId', '==', agencyId),
    limit(1)
  )

  const snapshot = await getDocs(q)
  if (snapshot.empty) return null

  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as VisitNote
}

// Get all visit notes for a caregiver
export async function getCaregiverVisitNotes(
  caregiverId: string,
  agencyId: string
): Promise<VisitNote[]> {
  const q = query(
    collection(db, COLLECTION),
    where('caregiverId', '==', caregiverId),
    where('agencyId', '==', agencyId),
    orderBy('createdAt', 'desc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VisitNote))
}

// Get all visit notes for a client
export async function getClientVisitNotes(
  clientId: string,
  agencyId: string,
  limitCount?: number
): Promise<VisitNote[]> {
  let q = query(
    collection(db, COLLECTION),
    where('clientId', '==', clientId),
    where('agencyId', '==', agencyId),
    orderBy('createdAt', 'desc')
  )

  if (limitCount) {
    q = query(q, limit(limitCount))
  }

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VisitNote))
}

// Get visit notes pending review
export async function getPendingVisitNotes(agencyId: string): Promise<VisitNote[]> {
  const q = query(
    collection(db, COLLECTION),
    where('agencyId', '==', agencyId),
    where('status', '==', 'submitted'),
    orderBy('createdAt', 'desc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as VisitNote))
}

// Notes that are currently locked for the caregiver — submitted or approved
// are auto-locked (caregiver can't self-edit), so admin/supervisor may want
// to unlock them from the Unlock Requests page.
// Filters out notes that currently hold a valid unlockOverride (still within
// the expiry window) since those are effectively already unlocked.
export async function getLockedVisitNotes(agencyId: string): Promise<VisitNote[]> {
  const q = query(
    collection(db, COLLECTION),
    where('agencyId', '==', agencyId),
    where('status', 'in', ['submitted', 'approved']),
    orderBy('createdAt', 'desc')
  )
  const snapshot = await getDocs(q)
  const now = Date.now()
  return snapshot.docs
    .map(d => ({ id: d.id, ...d.data() } as VisitNote))
    .filter(note => {
      const override = (note as unknown as { unlockOverride?: { expiresAt?: { toDate?: () => Date } | Date } }).unlockOverride
      if (!override?.expiresAt) return true
      const expiresAt =
        typeof (override.expiresAt as { toDate?: () => Date }).toDate === 'function'
          ? (override.expiresAt as { toDate: () => Date }).toDate()
          : (override.expiresAt as Date)
      return expiresAt.getTime() < now
    })
}

// Create an empty placeholder visit note. Used when a caregiver needs to
// file an unlock request on a shift they never started documenting — the
// unlock-request system keys off `visitNoteId`, so we need a doc to point
// at. The doc stays in `draft` status with empty fields until the caregiver
// actually fills it in and submits.
export async function createEmptyDraftVisitNote({
  agencyId,
  visitId,
  clientId,
  caregiverId,
  userId,
}: {
  agencyId: string
  visitId: string
  clientId: string
  caregiverId: string
  userId: string
}): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTION), {
    agencyId,
    visitId,
    clientId,
    caregiverId,
    summary: '',
    clientCondition: null,
    clientMood: null,
    activitiesCompleted: null,
    medicationsAdministered: [],
    vitalSigns: null,
    tasksCompleted: [],
    photos: null,
    signature: null,
    status: 'draft' as VisitNoteStatus,
    aiGenerated: false,
    aiTranscript: null,
    documentationTime: null,
    travelTime: null,
    timeIn: null,
    timeOut: null,
    hcpcsCode: null,
    locationCode: null,
    billable: true,
    clinicianCredentials: null,
    clinicalScales: null,
    isPlaceholder: true, // flag so cleanup/reports can tell this from a
                         // user-saved draft with real content
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })
  return docRef.id
}

// Create a new visit note
export async function createVisitNote({
  data,
  agencyId,
  caregiverId,
  userId,
  userEmail,
  userRole,
}: CreateVisitNoteParams): Promise<string> {
  // Upload signature if provided
  let signatureData: { url: string; signedAt: ReturnType<typeof serverTimestamp> } | undefined

  if (data.signature?.dataUrl) {
    const signatureRef = ref(storage, `agencies/${agencyId}/signatures/${data.visitId}_${Date.now()}.png`)
    await uploadString(signatureRef, data.signature.dataUrl, 'data_url')
    const signatureUrl = await getDownloadURL(signatureRef)
    signatureData = {
      url: signatureUrl,
      signedAt: serverTimestamp(),
    }
  }

  // Upload photos if provided
  let uploadedPhotos: VisitPhoto[] = []
  if (data.photos && data.photos.length > 0) {
    uploadedPhotos = await uploadVisitPhotos(agencyId, data.visitId, data.photos)
  }

  const noteData = {
    agencyId,
    visitId: data.visitId,
    clientId: data.clientId,
    caregiverId,
    summary: data.summary,
    clientCondition: data.clientCondition,
    clientMood: data.clientMood || null,
    activitiesCompleted: data.activitiesCompleted || null,
    medicationsAdministered: data.medicationsAdministered || [],
    vitalSigns: data.vitalSigns || null,
    tasksCompleted: data.tasksCompleted || [],
    photos: uploadedPhotos.length > 0 ? uploadedPhotos : null,
    signature: signatureData || null,
    status: 'submitted' as VisitNoteStatus,
    aiGenerated: data.aiGenerated || false,
    aiTranscript: data.aiTranscript || null,
    // Billing & Administrative
    documentationTime: data.documentationTime ?? null,
    travelTime: data.travelTime ?? null,
    timeIn: data.timeIn || null,
    timeOut: data.timeOut || null,
    hcpcsCode: data.hcpcsCode || null,
    locationCode: data.locationCode || null,
    billable: data.billable ?? true,
    clinicianCredentials: data.clinicianCredentials || null,
    clinicalScales: data.clinicalScales || null,
    // Visit-type flags
    missedVisit: data.missedVisit ?? false,
    missedVisitReason: data.missedVisitReason || null,
    prn: data.prn ?? false,
    external: data.external ?? false,
    // Generic attachments
    attachments: data.attachments && data.attachments.length > 0
      ? data.attachments.map(a => ({
          id: a.id,
          url: a.url,
          fileName: a.fileName,
          contentType: a.contentType,
          sizeBytes: a.sizeBytes,
          uploadedAt: a.uploadedAt instanceof Date ? Timestamp.fromDate(a.uploadedAt) : serverTimestamp(),
        }))
      : null,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  }

  const docRef = await addDoc(collection(db, COLLECTION), stripUndefinedDeep(noteData))

  const visitRef = doc(db, 'visits', data.visitId)
  const visitSnap = await getDoc(visitRef)
  const visitData = visitSnap.exists() ? ({ id: visitSnap.id, ...visitSnap.data() } as Visit) : null

  // Update visit with note reference
  await updateDoc(visitRef, {
    visitNoteId: docRef.id,
    status: 'completed',
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })

  if (visitData?.authorizationId && !visitData.visitNoteId) {
    try {
      const authorization = await getAuthorization(visitData.authorizationId)
      if (authorization && authorization.agencyId === agencyId) {
        const unitsToAdd = calculateVisitUnitsUsed(authorization, visitData)
        if (unitsToAdd > 0) {
          await updateUnitsUsed(
            authorization.id,
            unitsToAdd,
            agencyId,
            userId,
            userEmail,
            userRole
          )
        }
      }
    } catch (unitsError) {
      console.error('Failed to update authorization usage from visit completion:', unitsError)
    }
  }

  // Create audit log
  await createAuditLog({
    agencyId,
    userId,
    userEmail,
    userRole,
    action: 'create',
    resource: 'visit_note',
    resourceId: docRef.id,
    description: `Created visit note for visit ${data.visitId}`,
    newValues: { summary: data.summary, clientCondition: data.clientCondition },
  })

  return docRef.id
}

// Update a visit note
export async function updateVisitNote({
  noteId,
  data,
  agencyId,
  userId,
  userEmail,
  userRole,
}: UpdateVisitNoteParams): Promise<void> {
  const docRef = doc(db, COLLECTION, noteId)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) {
    throw new Error('Visit note not found')
  }

  const existingNote = docSnap.data()
  if (existingNote.agencyId !== agencyId) {
    throw new Error('Access denied')
  }

  // Lock enforcement — prevent edits on locked notes (unless temporarily unlocked)
  if (existingNote.visitId) {
    const visitDoc = await getDoc(doc(db, 'visits', existingNote.visitId))
    if (visitDoc.exists()) {
      const visitData = visitDoc.data()
      const settings = await getAgencySettings(agencyId)
      const noteLockHours = settings?.visitNotes?.noteLockHours ?? 24
      if (noteLockHours > 0 && visitData.scheduledEnd) {
        const lockStatus = getVisitNoteLockStatus({
          scheduledEnd: visitData.scheduledEnd,
          noteLockHours,
          unlockOverride: existingNote.unlockOverride,
        })
        if (lockStatus.isLocked && !lockStatus.isTemporarilyUnlocked) {
          throw new Error('This visit note is locked. It was locked 24 hours after the shift ended. Request an unlock from your supervisor to make changes.')
        }
      }
    }
  }

  // Deep-strip undefined values — Firestore rejects them at any nesting level
  const deepStrip = (obj: unknown): unknown => {
    if (obj === null || obj === undefined) return null
    if (obj instanceof Date) return obj
    if (Array.isArray(obj)) return obj.map(item => deepStrip(item))
    if (typeof obj === 'object') {
      const clean: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
        if (v !== undefined) {
          clean[k] = deepStrip(v)
        }
      }
      return clean
    }
    return obj
  }

  const cleanData = deepStrip(data) as Record<string, unknown>
  cleanData.updatedAt = serverTimestamp()
  cleanData.updatedBy = userId

  await updateDoc(docRef, cleanData as never)

  // Create audit log (strip undefined values to avoid Firestore rejection)
  const auditValues = deepStrip(data) as Record<string, unknown>
  await createAuditLog({
    agencyId,
    userId,
    userEmail,
    userRole,
    action: 'update',
    resource: 'visit_note',
    resourceId: noteId,
    description: `Updated visit note`,
    previousValues: { status: existingNote.status },
    newValues: auditValues,
  })
}

// Review a visit note (supervisor action)
export async function reviewVisitNote({
  noteId,
  status,
  comments,
  agencyId,
  userId,
  userEmail,
  userRole,
}: {
  noteId: string
  status: 'approved' | 'revision_requested'
  comments?: string
  agencyId: string
  userId: string
  userEmail: string
  userRole: UserRole
}): Promise<void> {
  const docRef = doc(db, COLLECTION, noteId)
  const docSnap = await getDoc(docRef)

  if (!docSnap.exists()) {
    throw new Error('Visit note not found')
  }

  const existingNote = docSnap.data()
  if (existingNote.agencyId !== agencyId) {
    throw new Error('Access denied')
  }

  await updateDoc(docRef, {
    status,
    revisionComment: status === 'revision_requested' ? (comments || null) : null,
    supervisorReview: {
      reviewedBy: userId,
      reviewedAt: serverTimestamp(),
      comments: comments || null,
    },
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  })

  // Create audit log
  await createAuditLog({
    agencyId,
    userId,
    userEmail,
    userRole,
    action: 'update',
    resource: 'visit_note',
    resourceId: noteId,
    description: `${status === 'approved' ? 'Approved' : 'Requested revision for'} visit note`,
    previousValues: { status: existingNote.status },
    newValues: { status, comments },
  })
}
